import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import ContentContainer from "../components/ContentContainer";
import PageHeader from "../components/PageHeader";
import {
  PathBuilderOptionsOS,
  PathBuilderOptionsShortcuts,
  PathBuilderOptionsSeedSigner,
  PathBuilderOptionsHat,
  PathBuilderOptionsJavaCard,
  PathBuilderOptionsSeedkeeper,
  PathBuilderOptionsSatochip,
  PathBuilderOptionsWalletConnection,
  PathBuilderOptionsKeyBackup,
  PathBuilderInstructionsOS,
  PathBuilderInstructionsHat,
  PathBuilderInstructionsSeedkeeper,
  PathBuilderInstructionsSatochip,
  PathBuilderInstructionsWalletConnection,
  PathBuilderInstructionsKeyBackup,
} from "../components/pathbuilder";
import StandardInstruction from "../components/StandardInstruction";
import BalenaEtcherDownload from "../components/BalenaEtcherDownload";
import ImageWithModal from "../components/ImageWithModal";
import ExpandableYouTubeEmbed from "../components/ExpandableYouTubeEmbed";
import { usePathBuilderOptions } from "../hooks/usePathBuilderOptions";
import { 
  getProductName, 
  getProductRepoName, 
  getProductCapFileName, 
  getProductRepoUrl,
  getOSName,
  getSelectedOS,
  hasOSSelected
} from "../utils/pathBuilderHelpers";
import "./PathBuilder.css";

function PathBuilder() {
  const { t, i18n } = useTranslation();
  const [, forceUpdate] = React.useState(0);
  const [copiedCommand, setCopiedCommand] = React.useState(false);
  const [blueWalletExpanded, setBlueWalletExpanded] = React.useState(false);
  const [bullWalletExpanded, setBullWalletExpanded] = React.useState(false);
  const [sparrowWalletExpanded, setSparrowWalletExpanded] = React.useState(false);
  
  // Use custom hook for state management
  const { options, handleOptionChange, handleReset } = usePathBuilderOptions();
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedCommand(true);
      setTimeout(() => setCopiedCommand(false), 2000);
    });
  };
  
  // Force re-render when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate(prev => prev + 1);
    };
    
    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  // OS must be selected before other options are enabled
  const osSelected = hasOSSelected(options);
  // JavaCard product selection
  const selectedJavaCardProduct = options.javaCardProduct; // 'satochip' | 'seedkeeper' | 'satodime' | null
  const isSatochipSelected = selectedJavaCardProduct === 'satochip';
  const isSeedkeeperSelected = selectedJavaCardProduct === 'seedkeeper';
  const isSatodimeSelected = selectedJavaCardProduct === 'satodime';
  
  // Helper functions using imported utilities
  const productName = getProductName(selectedJavaCardProduct, t);
  const productRepoName = getProductRepoName(selectedJavaCardProduct);
  const productCapFileName = getProductCapFileName(selectedJavaCardProduct);
  const productRepoUrl = getProductRepoUrl(selectedJavaCardProduct);
  const osName = getOSName(options, t);
  const selectedOS = getSelectedOS(options);
  
  return (
    <div className="path-builder">
      <PageHeader 
        title={t("pathBuilder.title")}
        intro={t("pathBuilder.intro")}
      />
      
      {/* Category-Based Checkbox Interface */}
      <ContentContainer>
        <div className="path-builder-interface">
          <div className="path-options-container">
            <PathBuilderOptionsOS t={t} options={options} handleOptionChange={handleOptionChange} />
            <PathBuilderOptionsShortcuts t={t} options={options} handleOptionChange={handleOptionChange} osSelected={osSelected} />
            <PathBuilderOptionsSeedSigner t={t} options={options} handleOptionChange={handleOptionChange} osSelected={osSelected} />
            <PathBuilderOptionsHat t={t} options={options} handleOptionChange={handleOptionChange} osSelected={osSelected} />
            <PathBuilderOptionsJavaCard
              t={t}
              options={options}
              handleOptionChange={handleOptionChange}
              osSelected={osSelected}
              productName={productName}
              isSeedkeeperSelected={isSeedkeeperSelected}
            />
            <PathBuilderOptionsSeedkeeper t={t} options={options} handleOptionChange={handleOptionChange} osSelected={osSelected} />
            <PathBuilderOptionsSatochip t={t} options={options} handleOptionChange={handleOptionChange} osSelected={osSelected} />
            <PathBuilderOptionsWalletConnection
              t={t}
              options={options}
              handleOptionChange={handleOptionChange}
              osSelected={osSelected}
              blueWalletExpanded={blueWalletExpanded}
              setBlueWalletExpanded={setBlueWalletExpanded}
              bullWalletExpanded={bullWalletExpanded}
              setBullWalletExpanded={setBullWalletExpanded}
              sparrowWalletExpanded={sparrowWalletExpanded}
              setSparrowWalletExpanded={setSparrowWalletExpanded}
            />
            <PathBuilderOptionsKeyBackup t={t} options={options} handleOptionChange={handleOptionChange} osSelected={osSelected} />
          </div>
          
          {/* Reset Button - Bottom Right of Checkbox Box */}
          <button 
            className="reset-form-button"
            onClick={handleReset}
            title={t("pathBuilder.checkboxes.resetForm")}
            aria-label={t("pathBuilder.checkboxes.resetForm")}
          >
            <span className="reset-icon">↻</span>
            <span className="reset-text">{t("pathBuilder.checkboxes.resetForm")}</span>
          </button>
        </div>
      </ContentContainer>

      {/* All Instruction Sections - Below checkbox interface */}
      {osSelected && (
        <ContentContainer>
          <div className="instruction-sections-container">
            <PathBuilderInstructionsOS t={t} options={options} osName={osName} />


            {/* SEEDSIGNER */}
            {(options.buySeedSigner || options.assembleSeedSigner || options.downloadInstallBalena || options.flashSeedSignerUseRelease || options.flashSeedSignerCompile || options.flashSeedSigner || options.verifySignature || options.verifyBitcoinSignature || options.configureSeedSignerSettings) && (
              <div className="instruction-category-group">
                <h3 className="instruction-category-header">{t("pathBuilder.categories.seedsigner")}</h3>
                {options.buySeedSigner && (
              <StandardInstruction
                title={t("pathBuilder.instructionTitles.sourceParts")}
                icon="🛒"
                intro={
                  <>
                    <p>{t("pathBuilder.instructionContent.sourcePartsIntro")}</p>
                    <p>
                      <a 
                        href="https://seedsigner.com/hardware/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="external-link"
                      >
                        {t("pathBuilder.instructionContent.sourcePartsOfficialLink")}
                      </a>
                      {t("pathBuilder.instructionContent.sourcePartsResourcesNote")}
                    </p>
                    <p>{t("pathBuilder.instructionContent.sourcePartsSecurityNote")}</p>
                    <p>
                      {t("pathBuilder.instructionContent.sourcePartsHousingNote")}
                      <a 
                        href="https://www.printables.com/model/1300792-seedsigner-smartcard-reader-hat-openpill-mini" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="external-link"
                      >
                        {t("pathBuilder.instructionContent.sourcePartsSmartcardLink")}
                      </a>
                      {t("pathBuilder.instructionContent.sourcePartsSmartcardNote2")}
                    </p>
                  </>
                }
                customContent={
                  <div className="component-list-section">
                    <h4 className="instruction-subsection-title">{t("pathBuilder.instructionContent.componentListTitle")}</h4>
                    <ul className="component-list">
                      <li>
                        {t("pathBuilder.instructionContent.component1")}
                        {" "}
                        <a 
                          href="https://www.raspberrypi.com/products/raspberry-pi-zero/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="external-link"
                        >
                          {t("pathBuilder.instructionContent.componentSpecs")}
                        </a>
                      </li>
                      <li>
                        {t("pathBuilder.instructionContent.component2")}
                        {" "}
                        <a 
                          href="https://www.waveshare.com/1.3inch-lcd-hat.htm" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="external-link"
                        >
                          {t("pathBuilder.instructionContent.componentSpecs")}
                        </a>
                      </li>
                      <li>
                        {t("pathBuilder.instructionContent.component3")}
                        {" "}
                        <a 
                          href="https://www.raspberrypi.com/documentation/accessories/camera.html" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="external-link"
                        >
                          {t("pathBuilder.instructionContent.componentSpecs")}
                        </a>
                      </li>
                      <li>
                        {t("pathBuilder.instructionContent.component4")}
                        {" "}
                        <a 
                          href="https://www.adafruit.com/product/2822" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="external-link"
                        >
                          {t("pathBuilder.instructionContent.componentSeeExample")}
                        </a>
                      </li>
                      <li>
                        {t("pathBuilder.instructionContent.component5")}
                        {" "}
                        <a 
                          href="https://www.amazon.com/s?k=microsd+card+32gb" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="external-link"
                        >
                          {t("pathBuilder.instructionContent.componentSeeExample")}
                        </a>
                      </li>
                      <li>
                        {t("pathBuilder.instructionContent.component6")}
                        {" "}
                        <a 
                          href="https://www.printables.com/model/1300792-seedsigner-smartcard-reader-hat-openpill-mini" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="external-link"
                        >
                          {t("pathBuilder.instructionContent.componentSpecs")}
                        </a>
                      </li>
                      <li>
                        {t("pathBuilder.instructionContent.component7")}
                        {" "}
                        <a 
                          href="https://satochip.io/product/seedsigner-extension-kit/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="external-link"
                        >
                          {t("pathBuilder.instructionContent.componentSpecs")}
                        </a>
                      </li>
                    </ul>
                  </div>
                }
              />
            )}

            {options.downloadInstallBalena && (
              <StandardInstruction
                title={t("pathBuilder.instructionTitles.downloadInstallBalena")}
                icon="⬇️"
                tools={{
                  items: [
                    t("pathBuilder.instructionContent.downloadInstallBalenaTool1"),
                    t("pathBuilder.instructionContent.downloadInstallBalenaTool2")
                  ]
                }}
                steps={{
                  items: [
                    {
                      content: t("pathBuilder.instructionContent.downloadInstallBalenaStep1"),
                      children: <BalenaEtcherDownload selectedOS={selectedOS} />
                    },
                    selectedOS === 'Windows' 
                      ? t("pathBuilder.instructionContent.downloadInstallBalenaStep2Windows")
                      : selectedOS === 'Mac'
                      ? t("pathBuilder.instructionContent.downloadInstallBalenaStep2Mac")
                      : t("pathBuilder.instructionContent.downloadInstallBalenaStep2Linux"),
                    selectedOS === 'Windows'
                      ? t("pathBuilder.instructionContent.downloadInstallBalenaStep3Windows")
                      : selectedOS === 'Mac'
                      ? t("pathBuilder.instructionContent.downloadInstallBalenaStep3Mac")
                      : t("pathBuilder.instructionContent.downloadInstallBalenaStep3Linux"),
                    {
                      content: (
                        <>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                            {t("pathBuilder.instructionContent.downloadInstallBalenaStep4")}
                            <img 
                              src={`${process.env.PUBLIC_URL}/assets/images/balena/balena-logo.png`}
                              alt="Balena Etcher"
                              style={{ 
                                height: '20px', 
                                width: 'auto',
                                verticalAlign: 'middle',
                                marginLeft: '4px'
                              }}
                            />
                          </span>
                          <div style={{ marginTop: '12px' }}>
                            <ImageWithModal
                              src={`${process.env.PUBLIC_URL}/assets/images/balena/balena-open.png`}
                              alt="Balena Etcher application window"
                            />
                </div>
                        </>
                      )
                    }
                  ]
                }}
              />
            )}

            {options.flashSeedSignerUseRelease && (
              <StandardInstruction
                id="instruction-get-seedsigner-image-release"
                title={t("pathBuilder.instructionTitles.getSeedSignerImageRelease")}
                icon="⬇️"
                steps={{
                  items: [
                    {
                      content: (
                        <>
                          {t("pathBuilder.instructionContent.getSeedSignerImageReleaseStep1")}
                          <div className="buy-link-container" style={{ marginTop: "10px", marginBottom: "10px" }}>
                            <a 
                              href="https://github.com/3rdIteration/seedsigner/releases/download/SS0.8.6%2BSatochip%2BEarthdiver-B4/seedsigner_os.SS0.8.6_Satochip_Earthdiver-B4_.pi0-smartcard.img.zip"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="buy-link"
                            >
                              {t("pathBuilder.instructionContent.getSeedSignerImageReleaseStep1Download")}
                            </a>
                </div>
                          <p>
                            {t("pathBuilder.instructionContent.getSeedSignerImageReleaseStep1Alternative")}{" "}
                            <a 
                              href="https://github.com/3rdIteration/seedsigner/releases"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="external-link"
                            >
                              {t("pathBuilder.instructionContent.getSeedSignerImageReleaseStep1Link")}
                            </a>
                          </p>
                          <p>
                            {t("pathBuilder.instructionContent.getSeedSignerImageReleaseStep1Verify")}{" "}
                            {!options.verifySignature && (
                              <button
                                onClick={() => {
                                  // First ensure flashSeedSignerUseRelease is enabled (required for verifySignature)
                                  if (!options.flashSeedSignerUseRelease) {
                                    handleOptionChange('flashSeedSignerUseRelease');
                                  }
                                  // Then enable verifySignature after a brief delay to ensure state is updated
                                  setTimeout(() => {
                                    handleOptionChange('verifySignature');
                                    // Scroll to verify signature section
                                    setTimeout(() => {
                                      const element = document.getElementById('instruction-verify-signature');
                                      if (element) {
                                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                      }
                                    }, 100);
                                  }, 50);
                                }}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: '#667eea',
                                  textDecoration: 'underline',
                                  cursor: 'pointer',
                                  padding: 0,
                                  font: 'inherit',
                                  fontWeight: 600
                                }}
                                onMouseEnter={(e) => e.target.style.color = '#764ba2'}
                                onMouseLeave={(e) => e.target.style.color = '#667eea'}
                              >
                                {t("pathBuilder.instructionContent.getSeedSignerImageReleaseStep1VerifyLink")}
                              </button>
                            )}
                          </p>
                        </>
                      )
                    },
                    t("pathBuilder.instructionContent.getSeedSignerImageReleaseStep2")
                  ]
                }}
              />
            )}

            {options.verifySignature && (
              <StandardInstruction
                id="instruction-verify-signature"
                title={t("pathBuilder.instructionTitles.verifySignature")}
                icon="🔐"
                intro={
                  <p>
                    {t("pathBuilder.instructionContent.verifySignatureIntro")}{" "}
                    {!options.flashSeedSignerUseRelease && (
                      <button
                        onClick={() => {
                          handleOptionChange('flashSeedSignerUseRelease');
                          // Scroll to the download release section after a brief delay
                          setTimeout(() => {
                            const element = document.getElementById('instruction-get-seedsigner-image-release');
                            if (element) {
                              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                          }, 100);
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#667eea',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          padding: 0,
                          font: 'inherit',
                          fontWeight: 600
                        }}
                        onMouseEnter={(e) => e.target.style.color = '#764ba2'}
                        onMouseLeave={(e) => e.target.style.color = '#667eea'}
                      >
                        {t("pathBuilder.instructionContent.verifySignatureIntroLink")}
                      </button>
                    )}
                    {options.flashSeedSignerUseRelease && (
                      <button
                        onClick={() => {
                          const element = document.getElementById('instruction-get-seedsigner-image-release');
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#667eea',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          padding: 0,
                          font: 'inherit',
                          fontWeight: 600
                        }}
                        onMouseEnter={(e) => e.target.style.color = '#764ba2'}
                        onMouseLeave={(e) => e.target.style.color = '#667eea'}
                      >
                        {t("pathBuilder.instructionContent.verifySignatureIntroLink")}
                      </button>
                    )}
                  </p>
                }
                steps={{
                  items: [
                    {
                      content: (
                        <>
                          {t("pathBuilder.instructionContent.verifySignatureStep1")}
                          <div style={{ marginTop: "15px" }}>
                            {getSelectedOS(options) === 'Windows' && (
                              <div>
                                <p style={{ marginBottom: "8px", fontWeight: 600 }}>Windows (PowerShell):</p>
                                <div style={{ position: 'relative', marginBottom: "12px" }}>
                                  <pre style={{ 
                                    background: 'rgba(0, 0, 0, 0.3)', 
                                    padding: '12px 50px 12px 12px', 
                                    borderRadius: '6px', 
                                    overflow: 'auto',
                                    fontSize: '0.9em',
                                    lineHeight: '1.5',
                                    margin: 0
                                  }}>
                                    <code>Get-FileHash -Algorithm SHA256 'path\to\file.zip'</code>
                                  </pre>
                                  <button
                                    onClick={() => copyToClipboard("Get-FileHash -Algorithm SHA256 'path\\to\\file.zip'")}
                                    style={{
                                      position: 'absolute',
                                      top: '8px',
                                      right: '8px',
                                      padding: '8px',
                                      background: copiedCommand ? 'rgba(76, 175, 80, 0.8)' : 'rgba(102, 126, 234, 0.8)',
                                      border: 'none',
                                      borderRadius: '4px',
                                      color: 'white',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: '32px',
                                      height: '32px',
                                      transition: 'all 0.2s ease'
                                    }}
                                    title={copiedCommand ? "Copied!" : "Copy command"}
                                  >
                                    {copiedCommand ? (
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                      </svg>
                                    ) : (
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                      </svg>
                                    )}
                                  </button>
                                </div>
                                <p style={{ marginBottom: "8px", fontSize: "0.85em", color: "rgba(224, 224, 224, 0.7)" }}>
                                  Or if the file is in your Downloads folder:
                                </p>
                                <div style={{ position: 'relative' }}>
                                  <pre style={{ 
                                    background: 'rgba(0, 0, 0, 0.3)', 
                                    padding: '12px 50px 12px 12px', 
                                    borderRadius: '6px', 
                                    overflow: 'auto',
                                    fontSize: '0.9em',
                                    lineHeight: '1.5',
                                    margin: 0
                                  }}>
                                    <code>Get-FileHash -Algorithm SHA256 "$env:USERPROFILE\Downloads\seedsigner_os.SS0.8.6_Satochip_Earthdiver-B5_.pi0-smartcard.img.zip"</code>
                                  </pre>
                                  <button
                                    onClick={() => copyToClipboard('Get-FileHash -Algorithm SHA256 "$env:USERPROFILE\\Downloads\\seedsigner_os.SS0.8.6_Satochip_Earthdiver-B5_.pi0-smartcard.img.zip"')}
                                    style={{
                                      position: 'absolute',
                                      top: '8px',
                                      right: '8px',
                                      padding: '8px',
                                      background: copiedCommand ? 'rgba(76, 175, 80, 0.8)' : 'rgba(102, 126, 234, 0.8)',
                                      border: 'none',
                                      borderRadius: '4px',
                                      color: 'white',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: '32px',
                                      height: '32px',
                                      transition: 'all 0.2s ease'
                                    }}
                                    title={copiedCommand ? "Copied!" : "Copy command"}
                                  >
                                    {copiedCommand ? (
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                      </svg>
                                    ) : (
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                      </svg>
                                    )}
                                  </button>
                                </div>
                                <p style={{ marginTop: "8px", fontSize: "0.85em", color: "rgba(224, 224, 224, 0.7)" }}>
                                  Replace the filename with your actual downloaded file name if different
                                </p>
                              </div>
                            )}
                            {getSelectedOS(options) === 'Mac' && (
                              <div>
                                <p style={{ marginBottom: "8px", fontWeight: 600 }}>macOS (Terminal):</p>
                                <pre style={{ 
                                  background: 'rgba(0, 0, 0, 0.3)', 
                                  padding: '12px', 
                                  borderRadius: '6px', 
                                  overflow: 'auto',
                                  fontSize: '0.9em',
                                  lineHeight: '1.5'
                                }}>
                                  <code>shasum -a 256 'path/to/file.zip'</code>
                                </pre>
                                <p style={{ marginTop: "8px", fontSize: "0.85em", color: "rgba(224, 224, 224, 0.7)" }}>
                                  Replace 'path/to/file.zip' with the actual path to your downloaded .img.zip file
                                </p>
                              </div>
                            )}
                            {getSelectedOS(options) === 'Linux' && (
                              <div>
                                <p style={{ marginBottom: "8px", fontWeight: 600 }}>Linux (Terminal):</p>
                                <pre style={{ 
                                  background: 'rgba(0, 0, 0, 0.3)', 
                                  padding: '12px', 
                                  borderRadius: '6px', 
                                  overflow: 'auto',
                                  fontSize: '0.9em',
                                  lineHeight: '1.5'
                                }}>
                                  <code>sha256sum 'path/to/file.zip'</code>
                                </pre>
                                <p style={{ marginTop: "8px", fontSize: "0.85em", color: "rgba(224, 224, 224, 0.7)" }}>
                                  Replace 'path/to/file.zip' with the actual path to your downloaded .img.zip file
                                </p>
                              </div>
                            )}
                            {!getSelectedOS(options) && (
                              <div>
                                <p style={{ marginBottom: "8px", fontWeight: 600 }}>Windows (PowerShell):</p>
                                <div style={{ position: 'relative', marginBottom: "12px" }}>
                                  <pre style={{ 
                                    background: 'rgba(0, 0, 0, 0.3)', 
                                    padding: '12px 50px 12px 12px', 
                                    borderRadius: '6px', 
                                    overflow: 'auto',
                                    fontSize: '0.9em',
                                    lineHeight: '1.5',
                                    margin: 0
                                  }}>
                                    <code>Get-FileHash -Algorithm SHA256 'path\to\file.zip'</code>
                                  </pre>
                                  <button
                                    onClick={() => copyToClipboard("Get-FileHash -Algorithm SHA256 'path\\to\\file.zip'")}
                                    style={{
                                      position: 'absolute',
                                      top: '8px',
                                      right: '8px',
                                      padding: '8px',
                                      background: copiedCommand ? 'rgba(76, 175, 80, 0.8)' : 'rgba(102, 126, 234, 0.8)',
                                      border: 'none',
                                      borderRadius: '4px',
                                      color: 'white',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: '32px',
                                      height: '32px',
                                      transition: 'all 0.2s ease'
                                    }}
                                    title={copiedCommand ? "Copied!" : "Copy command"}
                                  >
                                    {copiedCommand ? (
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                      </svg>
                                    ) : (
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                      </svg>
                                    )}
                                  </button>
                                </div>
                                <p style={{ marginBottom: "8px", fontSize: "0.85em", color: "rgba(224, 224, 224, 0.7)" }}>
                                  Or if the file is in your Downloads folder:
                                </p>
                                <div style={{ position: 'relative', marginBottom: "15px" }}>
                                  <pre style={{ 
                                    background: 'rgba(0, 0, 0, 0.3)', 
                                    padding: '12px 50px 12px 12px', 
                                    borderRadius: '6px', 
                                    overflow: 'auto',
                                    fontSize: '0.9em',
                                    lineHeight: '1.5',
                                    margin: 0
                                  }}>
                                    <code>Get-FileHash -Algorithm SHA256 "$env:USERPROFILE\Downloads\seedsigner_os.SS0.8.6_Satochip_Earthdiver-B5_.pi0-smartcard.img.zip"</code>
                                  </pre>
                                  <button
                                    onClick={() => copyToClipboard('Get-FileHash -Algorithm SHA256 "$env:USERPROFILE\\Downloads\\seedsigner_os.SS0.8.6_Satochip_Earthdiver-B5_.pi0-smartcard.img.zip"')}
                                    style={{
                                      position: 'absolute',
                                      top: '8px',
                                      right: '8px',
                                      padding: '8px',
                                      background: copiedCommand ? 'rgba(76, 175, 80, 0.8)' : 'rgba(102, 126, 234, 0.8)',
                                      border: 'none',
                                      borderRadius: '4px',
                                      color: 'white',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: '32px',
                                      height: '32px',
                                      transition: 'all 0.2s ease'
                                    }}
                                    title={copiedCommand ? "Copied!" : "Copy command"}
                                  >
                                    {copiedCommand ? (
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                      </svg>
                                    ) : (
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                      </svg>
                                    )}
                                  </button>
                                </div>
                                <p style={{ marginBottom: "8px", fontWeight: 600 }}>macOS/Linux (Terminal):</p>
                                <pre style={{ 
                                  background: 'rgba(0, 0, 0, 0.3)', 
                                  padding: '12px', 
                                  borderRadius: '6px', 
                                  overflow: 'auto',
                                  fontSize: '0.9em',
                                  lineHeight: '1.5'
                                }}>
                                  <code>shasum -a 256 'path/to/file.zip'  # macOS{'\n'}sha256sum 'path/to/file.zip'  # Linux</code>
                                </pre>
                                <p style={{ marginTop: "8px", fontSize: "0.85em", color: "rgba(224, 224, 224, 0.7)" }}>
                                  Replace 'path/to/file.zip' with the actual path to your downloaded .img.zip file
                                </p>
                              </div>
                            )}
                          </div>
                        </>
                      )
                    },
                    {
                      content: (
                        <>
                          {t("pathBuilder.instructionContent.verifySignatureStep2")}{" "}
                          <a 
                            href="https://github.com/3rdIteration/seedsigner/releases"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="external-link"
                          >
                            {t("pathBuilder.instructionContent.verifySignatureStep2Link")}
                          </a>
                          {t("pathBuilder.instructionContent.verifySignatureStep2Rest")}
                        </>
                      )
                    },
                    {
                      content: (
                        <>
                          {t("pathBuilder.instructionContent.verifySignatureStep3")}
                          <div style={{ marginTop: "15px" }}>
                            <p style={{ marginBottom: "8px", fontWeight: 600 }}>{t("pathBuilder.instructionContent.verifySignatureStep3ChecksumsTitle")}</p>
                            <pre style={{ 
                              background: 'rgba(0, 0, 0, 0.3)', 
                              padding: '12px', 
                              borderRadius: '6px', 
                              overflow: 'auto',
                              fontSize: '0.85em',
                              lineHeight: '1.6',
                              fontFamily: 'monospace'
                            }}>
                              <code>{`43a8bccc90d3b067cc82b9be5b905724eb6a29b020e28ab76ab3470c9c64be9c  seedsigner_os.SS0.8.6_Satochip_Earthdiver-B5_.pi0-smartcard.img.zip`}</code>
                            </pre>
                            <p style={{ marginTop: "8px", fontSize: "0.85em", color: "rgba(224, 224, 224, 0.7)" }}>
                              {t("pathBuilder.instructionContent.verifySignatureStep3ChecksumsNote")}
                            </p>
                          </div>
                        </>
                      )
                    }
                  ]
                }}
              />
            )}

            {options.verifyBitcoinSignature && (
              <StandardInstruction
                id="instruction-verify-bitcoin-signature"
                title={t("pathBuilder.instructionTitles.verifyBitcoinSignature")}
                icon="₿"
                intro={t("pathBuilder.instructionContent.verifyBitcoinSignatureIntro")}
                steps={{
                  items: [
                    {
                      content: (
                        <>
                          <p style={{ marginBottom: "8px" }}>{t("pathBuilder.instructionContent.verifyBitcoinSignatureStep1")}</p>
                          <div style={{
                            background: 'rgba(102, 126, 234, 0.1)',
                            border: '1px solid rgba(102, 126, 234, 0.3)',
                            borderRadius: '6px',
                            padding: '12px',
                            marginTop: "8px"
                          }}>
                            <p style={{ margin: 0, fontSize: "0.9em", color: "rgba(224, 224, 224, 0.9)" }}>
                              {t("pathBuilder.instructionContent.verifyBitcoinSignatureStep1Explanation")}
                            </p>
                          </div>
                        </>
                      )
                    },
                    t("pathBuilder.instructionContent.verifyBitcoinSignatureStep2"),
                    {
                      content: (
                        <>
                          {t("pathBuilder.instructionContent.verifyBitcoinSignatureStep3")}{" "}
                          <a
                            href="https://www.verifybitcoinmessage.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="external-link"
                          >
                            {t("pathBuilder.instructionContent.verifyBitcoinSignatureStep3Link1")}
                          </a>
                          {" "}{t("pathBuilder.instructionContent.verifyBitcoinSignatureStep3LinkSeparator", { defaultValue: "or" })}{" "}
                          <a
                            href="https://bitaps.com/signature"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="external-link"
                          >
                            {t("pathBuilder.instructionContent.verifyBitcoinSignatureStep3Link2")}
                          </a>
                        </>
                      )
                    },
                    t("pathBuilder.instructionContent.verifyBitcoinSignatureStep4"),
                    t("pathBuilder.instructionContent.verifyBitcoinSignatureStep5"),
                    t("pathBuilder.instructionContent.verifyBitcoinSignatureStep6"),
                    t("pathBuilder.instructionContent.verifyBitcoinSignatureStep7")
                  ]
                }}
              />
            )}

            {options.flashSeedSigner && (
              <StandardInstruction
                title={t("pathBuilder.instructionTitles.flashSeedSigner")}
                icon="💾"
                tools={{
                  items: [
                    t("pathBuilder.instructionContent.flashSeedSignerTool1"),
                    t("pathBuilder.instructionContent.flashSeedSignerTool2"),
                    t("pathBuilder.instructionContent.flashSeedSignerTool3")
                  ]
                }}
                steps={{
                  items: [
                    t("pathBuilder.instructionContent.flashSeedSignerStep1"),
                    t("pathBuilder.instructionContent.flashSeedSignerStep2"),
                    {
                      content: (
                        <>
                          {t("pathBuilder.instructionContent.flashSeedSignerStep3")}
                          <div style={{ marginTop: '12px' }}>
                            <ImageWithModal
                              src={`${process.env.PUBLIC_URL}/assets/images/balena/balena-file.png`}
                              alt="Balena Etcher - Flash from file"
                            />
                </div>
                        </>
                      )
                    },
                    {
                      content: (
                        <>
                          {t("pathBuilder.instructionContent.flashSeedSignerStep4")}
                          <div style={{ marginTop: '12px' }}>
                            <ImageWithModal
                              src={`${process.env.PUBLIC_URL}/assets/images/balena/balena-microsd.png`}
                              alt="Balena Etcher - Select target microSD card"
                            />
                          </div>
                        </>
                      )
                    },
                    {
                      content: (
                        <>
                          {t("pathBuilder.instructionContent.flashSeedSignerStep5")}
                          <div style={{ marginTop: '12px' }}>
                            <ImageWithModal
                              src={`${process.env.PUBLIC_URL}/assets/images/balena/balena-done.png`}
                              alt="Balena Etcher - Ready to flash"
                            />
                          </div>
                        </>
                      )
                    },
                    {
                      content: (
                        <>
                          {t("pathBuilder.instructionContent.flashSeedSignerStep6")}
                          <div style={{ marginTop: '12px' }}>
                            <ImageWithModal
                              src={`${process.env.PUBLIC_URL}/assets/images/balena/balena-progress.png`}
                              alt="Balena Etcher - Flashing progress"
                            />
                          </div>
                        </>
                      )
                    },
                    {
                      content: (
                        <>
                          {t("pathBuilder.instructionContent.flashSeedSignerStep7")}
                          <div style={{ marginTop: '12px' }}>
                            <ImageWithModal
                              src={`${process.env.PUBLIC_URL}/assets/images/balena/balena-completed.png`}
                              alt="Balena Etcher - Flashing complete"
                            />
                          </div>
                        </>
                      )
                    },
                    t("pathBuilder.instructionContent.flashSeedSignerStep8")
                  ]
                }}
                note={{
                  label: t("pathBuilder.instructionContent.importantNote"),
                  text: t("pathBuilder.instructionContent.seedSignerNonPersistent")
                }}
              />
            )}

            {options.flashSeedSignerCompile && (
              <StandardInstruction
                title={t("pathBuilder.instructionTitles.getSeedSignerImageCompile")}
                icon="⚙️"
                steps={{
                  items: [
                    t("pathBuilder.instructionContent.getSeedSignerImageCompileStep1"),
                    t("pathBuilder.instructionContent.getSeedSignerImageCompileStep2"),
                    t("pathBuilder.instructionContent.getSeedSignerImageCompileStep3")
                  ]
                }}
              />
            )}

            {options.assembleSeedSigner && (
              <div className="assemble-seedsigner-wrapper">
                <StandardInstruction
                  title={t("pathBuilder.instructionTitles.assembleSeedSigner")}
                  icon="🔨"
                  tools={{
                    items: [
                      t("pathBuilder.instructionContent.assembleSeedSignerTool1"),
                      t("pathBuilder.instructionContent.assembleSeedSignerTool2"),
                      t("pathBuilder.instructionContent.assembleSeedSignerTool3"),
                      t("pathBuilder.instructionContent.assembleSeedSignerTool4"),
                      t("pathBuilder.instructionContent.assembleSeedSignerTool5"),
                      t("pathBuilder.instructionContent.assembleSeedSignerTool6"),
                      ...(options.buyHat ? [t("pathBuilder.instructionContent.assembleSeedSignerTool7")] : []),
                      t("pathBuilder.instructionContent.assembleSeedSignerTool8")
                    ]
                  }}
                  intro={
                    <ExpandableYouTubeEmbed 
                      videoId="https://www.youtube.com/watch?v=jqE8HDMCImA"
                      title={t("pathBuilder.instructionContent.assembleSeedSignerVideoLink")}
                    />
                  }
                  steps={{
                    items: [
                      {
                        content: (
                          <>
                            <div style={{ marginBottom: '16px' }}>
                              <strong>{t("pathBuilder.instructionContent.assembleSeedSignerStep1Title")}</strong>
                            </div>
                            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginBottom: '12px' }}>
                              <div style={{ flex: '1', minWidth: 0 }}>
                                {t("pathBuilder.instructionContent.assembleSeedSignerStep1a")}
                              </div>
                              <div style={{ flexShrink: 0 }}>
                                <ImageWithModal
                                  src={`${process.env.PUBLIC_URL}/assets/images/seedsigner/image_camera_09.jpg`}
                                  alt={t("pathBuilder.instructionContent.assembleSeedSignerCamera09Alt")}
                                />
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginBottom: '12px' }}>
                              <div style={{ flex: '1', minWidth: 0 }}>
                                {t("pathBuilder.instructionContent.assembleSeedSignerStep1b")}
                              </div>
                              <div style={{ flexShrink: 0 }}>
                                <ImageWithModal
                                  src={`${process.env.PUBLIC_URL}/assets/images/seedsigner/image_camera_07.jpg`}
                                  alt={t("pathBuilder.instructionContent.assembleSeedSignerCamera07Alt")}
                                />
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginBottom: '12px' }}>
                              <div style={{ flex: '1', minWidth: 0 }}>
                                {t("pathBuilder.instructionContent.assembleSeedSignerStep1c")}
                              </div>
                              <div style={{ flexShrink: 0 }}>
                                <ImageWithModal
                                  src={`${process.env.PUBLIC_URL}/assets/images/seedsigner/image_camera_06.jpg`}
                                  alt={t("pathBuilder.instructionContent.assembleSeedSignerCamera06Alt")}
                                />
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginBottom: '12px' }}>
                              <div style={{ flex: '1', minWidth: 0 }}>
                                {t("pathBuilder.instructionContent.assembleSeedSignerStep1d")}
                              </div>
                              <div style={{ flexShrink: 0 }}>
                                <ImageWithModal
                                  src={`${process.env.PUBLIC_URL}/assets/images/seedsigner/image_camera_08.jpg`}
                                  alt={t("pathBuilder.instructionContent.assembleSeedSignerCamera08Alt")}
                                />
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginBottom: '12px' }}>
                              <div style={{ flex: '1', minWidth: 0 }}>
                                {t("pathBuilder.instructionContent.assembleSeedSignerStep1d2")}
                              </div>
                              <div style={{ flexShrink: 0 }}>
                                <ImageWithModal
                                  src={`${process.env.PUBLIC_URL}/assets/images/seedsigner/image_camera_10.jpg`}
                                  alt={t("pathBuilder.instructionContent.assembleSeedSignerCamera10Alt")}
                                />
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginBottom: '12px' }}>
                              <div style={{ flex: '1', minWidth: 0 }}>
                                {t("pathBuilder.instructionContent.assembleSeedSignerStep1e")}
                              </div>
                              <div style={{ flexShrink: 0 }}>
                                <ImageWithModal
                                  src={`${process.env.PUBLIC_URL}/assets/images/seedsigner/image_camera_01.jpg`}
                                  alt={t("pathBuilder.instructionContent.assembleSeedSignerCamera01Alt")}
                                />
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginBottom: '12px' }}>
                              <div style={{ flex: '1', minWidth: 0 }}>
                                {t("pathBuilder.instructionContent.assembleSeedSignerStep1f")}
                              </div>
                              <div style={{ flexShrink: 0 }}>
                                <ImageWithModal
                                  src={`${process.env.PUBLIC_URL}/assets/images/seedsigner/image_camera_02.jpg`}
                                  alt={t("pathBuilder.instructionContent.assembleSeedSignerCamera02Alt")}
                                />
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginBottom: '12px' }}>
                              <div style={{ flex: '1', minWidth: 0 }}>
                                {t("pathBuilder.instructionContent.assembleSeedSignerStep1g")}
                              </div>
                              <div style={{ flexShrink: 0 }}>
                                <ImageWithModal
                                  src={`${process.env.PUBLIC_URL}/assets/images/seedsigner/image_camera_03.jpg`}
                                  alt={t("pathBuilder.instructionContent.assembleSeedSignerCamera03Alt")}
                                />
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginBottom: '12px' }}>
                              <div style={{ flex: '1', minWidth: 0 }}>
                                {t("pathBuilder.instructionContent.assembleSeedSignerStep1h")}
                              </div>
                              <div style={{ flexShrink: 0 }}>
                                <ImageWithModal
                                  src={`${process.env.PUBLIC_URL}/assets/images/seedsigner/image_camera_04.jpg`}
                                  alt={t("pathBuilder.instructionContent.assembleSeedSignerCamera04Alt")}
                                />
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                              <div style={{ flex: '1', minWidth: 0 }}>
                                {t("pathBuilder.instructionContent.assembleSeedSignerStep1i")}
                              </div>
                              <div style={{ flexShrink: 0 }}>
                                <ImageWithModal
                                  src={`${process.env.PUBLIC_URL}/assets/images/seedsigner/image_camera_05.jpg`}
                                  alt={t("pathBuilder.instructionContent.assembleSeedSignerCamera05Alt")}
                                />
                              </div>
                            </div>
                          </>
                        )
                      },
                      {
                        content: (
                          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                            <div style={{ flex: '1', minWidth: 0 }}>
                              {t("pathBuilder.instructionContent.assembleSeedSignerStep2")}
                            </div>
                            <div style={{ flexShrink: 0 }}>
                              <ImageWithModal
                                src={`${process.env.PUBLIC_URL}/assets/images/seedsigner/image_video_02.png`}
                                alt={t("pathBuilder.instructionContent.assembleSeedSignerImage2Alt")}
                              />
                            </div>
                          </div>
                        )
                      },
                      t("pathBuilder.instructionContent.assembleSeedSignerStep3"),
                      t("pathBuilder.instructionContent.assembleSeedSignerStep4"),
                      {
                        content: (
                          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                            <div style={{ flex: '1', minWidth: 0 }}>
                              {t("pathBuilder.instructionContent.assembleSeedSignerStep5")}
                            </div>
                            <div style={{ flexShrink: 0 }}>
                              <ImageWithModal
                                src={`${process.env.PUBLIC_URL}/assets/images/seedsigner/image_video_04.png`}
                                alt={t("pathBuilder.instructionContent.assembleSeedSignerImage4Alt")}
                              />
                            </div>
                          </div>
                        )
                      },
                      {
                        content: (
                          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                            <div style={{ flex: '1', minWidth: 0 }}>
                              {t("pathBuilder.instructionContent.assembleSeedSignerStep6")}
                            </div>
                            <div style={{ flexShrink: 0 }}>
                              <ImageWithModal
                                src={`${process.env.PUBLIC_URL}/assets/images/seedsigner/image_video_03.png`}
                                alt={t("pathBuilder.instructionContent.assembleSeedSignerImage3Alt")}
                              />
                            </div>
                          </div>
                        )
                      },
                      {
                        content: (
                          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                            <div style={{ flex: '1', minWidth: 0 }}>
                              {t("pathBuilder.instructionContent.assembleSeedSignerStep7")}
                            </div>
                            <div style={{ flexShrink: 0 }}>
                              <ImageWithModal
                                src={`${process.env.PUBLIC_URL}/assets/images/seedsigner/image_video_05.png`}
                                alt={t("pathBuilder.instructionContent.assembleSeedSignerImage5Alt")}
                              />
                            </div>
                          </div>
                        )
                      },
                      {
                        content: (
                          <>
                            {t("pathBuilder.instructionContent.assembleSeedSignerStep8")}
                            {!options.flashSeedSignerUseRelease && !options.flashSeedSignerCompile && (
                              <>
                                {" "}
                                <button
                                  onClick={() => handleOptionChange('flashSeedSignerUseRelease')}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#667eea',
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                    padding: 0,
                                    font: 'inherit',
                                    fontWeight: 600
                                  }}
                                  onMouseEnter={(e) => e.target.style.color = '#764ba2'}
                                  onMouseLeave={(e) => e.target.style.color = '#667eea'}
                                >
                                  {t("pathBuilder.instructionContent.assembleSeedSignerStep8Link")}
                                </button>
                              </>
                            )}
                          </>
                        )
                      },
                      {
                        content: (
                          <>
                            {t("pathBuilder.instructionContent.assembleSeedSignerStep9")}
                          </>
                        )
                      }
                    ]
                  }}
                  note={{
                    label: t("pathBuilder.instructionContent.importantNote"),
                    text: t("pathBuilder.instructionContent.assembleSeedSignerNote")
                  }}
                />
              </div>
            )}

            {options.configureSeedSignerSettings && (
              <StandardInstruction
                title={t("pathBuilder.instructionTitles.configureSeedSignerSettings")}
                icon="⚙️"
                intro={t("pathBuilder.instructionContent.configureSeedSignerSettingsIntro")}
                steps={{
                  items: [
                    {
                      content: (
                        <>
                          {t("pathBuilder.instructionContent.configureSeedSignerSettingsStep1")}{" "}
                          <a 
                            href="https://seedsigner.github.io/seedsigner-settings-generator/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="external-link"
                          >
                            {t("pathBuilder.instructionContent.configureSeedSignerSettingsStep1Link")}
                          </a>
                        </>
                      )
                    },
                    t("pathBuilder.instructionContent.configureSeedSignerSettingsStep2"),
                    t("pathBuilder.instructionContent.configureSeedSignerSettingsStep3"),
                    t("pathBuilder.instructionContent.configureSeedSignerSettingsStep4"),
                    t("pathBuilder.instructionContent.configureSeedSignerSettingsStep5")
                  ]
                }}
                note={{
                  label: t("pathBuilder.instructionContent.importantNote"),
                  text: t("pathBuilder.instructionContent.configureSeedSignerSettingsNote")
                }}
              />
            )}
               </div>
             )}

            <PathBuilderInstructionsHat t={t} options={options} />

            {/* JAVACARD */}
            {(options.buyJavaCardReady || options.buyJavaCardBlank || options.flashJavaCardViaSeedSigner || options.flashJavaCardDesktopCompile || options.flashJavaCardDesktopDownload || options.verifySignatureJavaCardDesktop || options.addRemoveApplet || options.removeAppletViaSeedSigner || options.createSeedkeeper || options.useSeedkeeper || options.backupSeedkeeper || options.restoreFromSeedkeeper) && (
              <div className="instruction-category-group">
                <h3 className="instruction-category-header">{t("pathBuilder.categories.javacard")}</h3>
                <div style={{ 
                  marginBottom: "20px", 
                  padding: "16px", 
                  background: "rgba(255, 152, 0, 0.15)", 
                  border: "2px solid rgba(255, 152, 0, 0.4)", 
                  borderRadius: "8px",
                  borderLeft: "4px solid #ff9800"
                }}>
                  <p style={{ marginBottom: "12px", fontSize: "0.95em", color: "#e0e0e0", lineHeight: "1.6" }}>
                    <strong style={{ color: "#ff9800" }}>⚠️ {t("pathBuilder.instructionContent.javacardBackupWarningTitle")}</strong>
                  </p>
                  <p style={{ marginBottom: "8px", fontSize: "0.9em", color: "#b0b0b0", lineHeight: "1.6" }}>
                    {t("pathBuilder.instructionContent.javacardBackupWarningText")}
                  </p>
                  <p style={{ marginBottom: "0", fontSize: "0.9em", color: "#b0b0b0", lineHeight: "1.6" }}>
                    {t("pathBuilder.instructionContent.javacardBackupWarningLink")}{" "}
                    <a 
                      href="#key-backup-section"
                      onClick={(e) => {
                        e.preventDefault();
                        const element = document.getElementById('key-backup-section');
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        } else {
                          // If section not visible yet, scroll to Key Backup checkbox
                          const checkbox = document.querySelector('[id*="backup-seed-seedor"]');
                          if (checkbox) {
                            checkbox.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }
                        }
                      }}
                      className="external-link"
                      style={{ color: "#ff9800" }}
                    >
                      {t("pathBuilder.instructionContent.javacardBackupWarningLinkText")}
                    </a>
                  </p>
                </div>
                <p style={{ marginBottom: "20px", fontSize: "0.95em", color: "rgba(224, 224, 224, 0.8)" }}>
                  {t("pathBuilder.instructionContent.javacardGitHubNote")}{" "}
                  <a 
                    href="https://github.com/Toporin"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="external-link"
                  >
                    {t("pathBuilder.instructionContent.javacardGitHubLink")}
                  </a>
                </p>
                {options.buyJavaCardReady && (
              <StandardInstruction
                title={t("pathBuilder.instructionTitles.buyJavaCardReady", { product: productName })}
                icon="🛒"
                steps={{
                  items: [
                    {
                      content: (
                        <>
                          {t("pathBuilder.instructionContent.buyJavaCardStep1", { product: productName })}{" "}
                {isSatochipSelected && (
                    <a 
                      href="https://satochip.io/product/satochip/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                              className="external-link"
                    >
                      {t("pathBuilder.instructionContent.buySatochipLink")}
                    </a>
                )}
                {isSeedkeeperSelected && (
                    <>
                      <a 
                        href="https://satochip.io/product/seedkeeper/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="external-link"
                      >
                        {t("pathBuilder.instructionContent.buySeedkeeperLink")}
                      </a>
                      <div style={{ marginTop: '12px' }}>
                        <ImageWithModal
                          src={`${process.env.PUBLIC_URL}/assets/images/seedsigner/image_11.jpg`}
                          alt="Seedkeeper unboxing - SEEDKEEPER by SATOCHIP product package"
                        />
                      </div>
                    </>
                )}
                {isSatodimeSelected && (
                    <a 
                      href="https://satochip.io/product/satodime/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                              className="external-link"
                    >
                      {t("pathBuilder.instructionContent.buySatodimeLink")}
                    </a>
                )}
                        </>
                      )
                    }
                  ]
                }}
                note={{
                  label: t("pathBuilder.instructionContent.nextStep"),
                  text: t("pathBuilder.instructionContent.buyJavaCardNextStep")
                }}
              />
            )}

            {options.buyJavaCardBlank && (
              <StandardInstruction
                title={t("pathBuilder.instructionTitles.buyJavaCardBlank")}
                icon="🛒"
                steps={{
                  items: [
                    {
                      content: (
                        <>
                          {t("pathBuilder.instructionContent.buyJavaCardBlankStep1")}{" "}
                          <a 
                            href="https://www.amazon.com/s?k=javacard+java+card+contact+smart+card" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="external-link"
                          >
                            {t("pathBuilder.instructionContent.buyJavaCardAmazonLink")}
                          </a>
                        </>
                      )
                    }
                  ]
                }}
                note={{
                  label: t("pathBuilder.instructionContent.importantNote"),
                  text: t("pathBuilder.instructionContent.buyJavaCardBlankNote", { product: productName })
                }}
              />
            )}

            {options.flashJavaCardViaSeedSigner && (
              <StandardInstruction
                title={t("pathBuilder.instructionTitles.flashJavaCardViaSeedSigner", { product: productName })}
                icon="💳"
                intro={
                  <ExpandableYouTubeEmbed 
                    videoId="https://youtu.be/NF4HemyEcOY?si=vDKmax_qbf8vW4Yl"
                    title={t("pathBuilder.instructionContent.flashJavaCardViaSeedSignerVideoLink")}
                  />
                }
                steps={{
                  items: [
                    t("pathBuilder.instructionContent.flashJavaCardViaSeedSignerStep1"),
                    t("pathBuilder.instructionContent.flashJavaCardViaSeedSignerStep2"),
                    {
                      content: (
                        <>
                          {t("pathBuilder.instructionContent.flashJavaCardViaSeedSignerStep3")}
                          <div style={{ marginTop: '12px' }}>
                            <ImageWithModal
                              src={`${process.env.PUBLIC_URL}/assets/images/seedsigner/images_flash_10.jpg`}
                              alt="SeedSigner device showing Tools menu with 'Smartcard Tools' option highlighted"
                            />
                          </div>
                        </>
                      )
                    },
                    {
                      content: (
                        <>
                          {t("pathBuilder.instructionContent.flashJavaCardViaSeedSignerStep4")}
                          <div style={{ marginTop: '12px' }}>
                            <ImageWithModal
                              src={`${process.env.PUBLIC_URL}/assets/images/seedsigner/images_flash_08.jpg`}
                              alt="SeedSigner device showing Smartcard Tools menu with 'DIY Tools' option highlighted"
                            />
                          </div>
                        </>
                      )
                    },
                    {
                      content: (
                        <>
                          {t("pathBuilder.instructionContent.flashJavaCardViaSeedSignerStep5", { product: productName })}
                          <div style={{ marginTop: '12px' }}>
                            <ImageWithModal
                              src={`${process.env.PUBLIC_URL}/assets/images/seedsigner/images_flash_05.jpg`}
                              alt="SeedSigner device showing Javacard DIY menu with 'Build Applets' option highlighted"
                            />
                          </div>
                        </>
                      )
                    },
                    {
                      content: (
                        <>
                          {t("pathBuilder.instructionContent.flashJavaCardViaSeedSignerStep6")}
                          <div style={{ marginTop: '12px' }}>
                            <ImageWithModal
                              src={`${process.env.PUBLIC_URL}/assets/images/seedsigner/images_flash_06.jpg`}
                              alt="SeedSigner device showing Javacard DIY menu with 'Install Applet' option highlighted"
                            />
                          </div>
                        </>
                      )
                    },
                    {
                      content: (
                        <>
                          {t("pathBuilder.instructionContent.flashJavaCardViaSeedSignerStep7", { product: productName })}
                          <div style={{ marginTop: '12px' }}>
                            <ImageWithModal
                              src={`${process.env.PUBLIC_URL}/assets/images/seedsigner/images_flash_07.jpg`}
                              alt="SeedSigner device showing applet selection menu with SatoChip applet highlighted"
                            />
                          </div>
                        </>
                      )
                    },
                    t("pathBuilder.instructionContent.flashJavaCardViaSeedSignerStep8"),
                    t("pathBuilder.instructionContent.flashJavaCardViaSeedSignerStep9"),
                    t("pathBuilder.instructionContent.flashJavaCardViaSeedSignerStep10")
                  ]
                }}
              />
            )}

            {options.flashJavaCardDesktopCompile && (
              <StandardInstruction
                title={t("pathBuilder.instructionTitles.flashJavaCardDesktopCompile", { product: productName })}
                icon="💻"
                tools={{
                  items: [
                    t("pathBuilder.instructionContent.flashJavaCardDesktopCompileTool1"),
                    t("pathBuilder.instructionContent.flashJavaCardDesktopCompileTool2"),
                    t("pathBuilder.instructionContent.flashJavaCardDesktopCompileTool3"),
                    t("pathBuilder.instructionContent.flashJavaCardDesktopCompileTool4")
                  ]
                }}
                steps={{
                  items: [
                    {
                      content: (
                        <>
                          {t("pathBuilder.instructionContent.flashJavaCardDesktopCompileStep1", { product: productName })}
                          <div className="code-block">
                            <code>{`git clone https://github.com/Toporin/${productRepoName}.git`}</code>
                          </div>
                          <p style={{ marginTop: "8px", fontSize: "0.9em" }}>
                            <a 
                              href={`https://github.com/Toporin/${productRepoName}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="external-link"
                            >
                              {t("pathBuilder.instructionContent.flashJavaCardDesktopCompileStep1Link", { product: productName })}
                            </a>
                          </p>
                        </>
                      )
                    },
                    {
                      content: (
                        <>
                          {t("pathBuilder.instructionContent.flashJavaCardDesktopCompileStep2")}
                          <ul>
                            <li>
                              {t("pathBuilder.instructionContent.flashJavaCardDesktopCompileStep2Sub1")}
                              <div className="code-block">
                                <code>wget https://github.com/martinpaljak/ant-javacard/releases/latest/download/ant-javacard.jar</code>
                              </div>
                              <div className="code-block">
                                <code>{`mv ant-javacard.jar ${productRepoName}/lib/`}</code>
                              </div>
                            </li>
                            <li>
                              {selectedOS === 'Linux' && (
                                <>
                                  {t("pathBuilder.instructionContent.flashJavaCardDesktopCompileStep2Sub2Linux")}
                                  <div className="code-block">
                                    <code>sudo apt install ant</code>
                                  </div>
                                </>
                              )}
                              {selectedOS === 'Mac' && (
                                <>
                                  {t("pathBuilder.instructionContent.flashJavaCardDesktopCompileStep2Sub2Mac")}
                                  <div className="code-block">
                                    <code>brew install ant</code>
                                  </div>
                                </>
                              )}
                              {selectedOS === 'Windows' && (
                                <>
                                  {t("pathBuilder.instructionContent.flashJavaCardDesktopCompileStep2Sub2Windows")}
                                </>
                              )}
                            </li>
                            <li>
                              {t("pathBuilder.instructionContent.flashJavaCardDesktopCompileStep2Sub3")}
                              <div className="code-block">
                                <code>{selectedOS === 'Windows' ? 'java -version' : 'sudo update-alternatives --config java'}</code>
                              </div>
                            </li>
                            <li>
                              {t("pathBuilder.instructionContent.flashJavaCardDesktopCompileStep2Sub4")}
                              <div className="code-block">
                                <code>{`cd ${productRepoName}`}</code>
                              </div>
                              <div className="code-block">
                                <code>git submodule add https://github.com/martinpaljak/oracle_javacard_sdks sdks</code>
                              </div>
                            </li>
                            <li>
                              {t("pathBuilder.instructionContent.flashJavaCardDesktopCompileStep2Sub5")}
                              <div className="code-block">
                                <code>ant</code>
                              </div>
                              <p>{t("pathBuilder.instructionContent.flashJavaCardDesktopCompileStep2Sub5Note", { product: productName })}</p>
                            </li>
                          </ul>
                        </>
                      )
                    },
                    {
                      content: (
                        <>
                          {t("pathBuilder.instructionContent.flashJavaCardDesktopCompileStep3")}
                          <div className="code-block">
                            <code>{t("pathBuilder.instructionContent.flashJavaCardDesktopCompileStep3Cmd", { product: productName })}</code>
                          </div>
                        </>
                      )
                    },
                    t("pathBuilder.instructionContent.flashJavaCardDesktopCompileStep4"),
                    {
                      content: (
                        <>
                          {t("pathBuilder.instructionContent.flashJavaCardDesktopCompileStep5")}
                          {selectedOS === 'Linux' && (
                            <div className="code-block">
                              <code>sudo apt install pcscd pcsc-tools</code>
                            </div>
                          )}
                          {selectedOS === 'Mac' && (
                            <div className="code-block">
                              <code>brew install pcsc-lite</code>
                            </div>
                          )}
                          <div className="code-block">
                            <code>git clone https://github.com/martinpaljak/GlobalPlatformPro.git</code>
                          </div>
                          <div className="code-block">
                            <code>cd GlobalPlatformPro</code>
                          </div>
                          <div className="code-block">
                            <code>./gradlew build</code>
                          </div>
                        </>
                      )
                    },
                    t("pathBuilder.instructionContent.flashJavaCardDesktopCompileStep6", { product: productName }),
                    {
                      content: (
                        <>
                          {t("pathBuilder.instructionContent.flashJavaCardDesktopCompileStep7", { product: productName })}
                          {selectedOS === 'Windows' && (
                            <>
                              <p><strong>{t("pathBuilder.instructionContent.flashJavaCardDesktopCompileStep7WindowsList")}</strong></p>
                              <div className="code-block">
                                <code>gp.exe -l</code>
                              </div>
                              <p><strong>{t("pathBuilder.instructionContent.flashJavaCardDesktopCompileStep7WindowsInstall")}</strong></p>
                              <div className="code-block">
                                <code>{`gp.exe -install .\\${productCapFileName}`}</code>
                              </div>
                              <p><strong>{t("pathBuilder.instructionContent.flashJavaCardDesktopCompileStep7WindowsUninstall")}</strong></p>
                              <div className="code-block">
                                <code>{`gp.exe -uninstall .\\${productCapFileName}`}</code>
                              </div>
                            </>
                          )}
                          {selectedOS === 'Linux' && (
                            <>
                              <p><strong>{t("pathBuilder.instructionContent.flashJavaCardDesktopCompileStep7LinuxList")}</strong></p>
                              <div className="code-block">
                                <code>java -jar gp.jar -l</code>
                              </div>
                              <p><strong>{t("pathBuilder.instructionContent.flashJavaCardDesktopCompileStep7LinuxInstall")}</strong></p>
                              <div className="code-block">
                                <code>{`java -jar gp.jar -install ./${productCapFileName}`}</code>
                              </div>
                              <p><strong>{t("pathBuilder.instructionContent.flashJavaCardDesktopCompileStep7LinuxUninstall")}</strong></p>
                              <div className="code-block">
                                <code>{`java -jar gp.jar -uninstall ./${productCapFileName}`}</code>
                              </div>
                            </>
                          )}
                          {selectedOS === 'Mac' && (
                            <>
                              <p><strong>{t("pathBuilder.instructionContent.flashJavaCardDesktopCompileStep7MacList")}</strong></p>
                              <div className="code-block">
                                <code>java -jar gp.jar -l</code>
                              </div>
                              <p><strong>{t("pathBuilder.instructionContent.flashJavaCardDesktopCompileStep7MacInstall")}</strong></p>
                              <div className="code-block">
                                <code>{`java -jar gp.jar -install ./${productCapFileName}`}</code>
                              </div>
                              <p><strong>{t("pathBuilder.instructionContent.flashJavaCardDesktopCompileStep7MacUninstall")}</strong></p>
                              <div className="code-block">
                                <code>{`java -jar gp.jar -uninstall ./${productCapFileName}`}</code>
                              </div>
                            </>
                          )}
                        </>
                      )
                    },
                    t("pathBuilder.instructionContent.flashJavaCardDesktopCompileStep8")
                  ]
                }}
                customContent={
                <div className="buy-link-container">
                  <a 
                    href="https://www.amazon.com/ACR39U-NF-PocketMate-USB-C-Smart-Reader/dp/B06X9NTGYV" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="buy-link"
                  >
                    {t("pathBuilder.instructionContent.flashJavaCardDesktopCompileLink")}
                  </a>
                </div>
                }
              />
            )}

            {options.flashJavaCardDesktopDownload && (
              <StandardInstruction
                title={t("pathBuilder.instructionTitles.flashJavaCardDesktopDownload", { product: productName })}
                icon="💻"
                tools={{
                  items: [
                    t("pathBuilder.instructionContent.flashJavaCardDesktopDownloadTool1"),
                    t("pathBuilder.instructionContent.flashJavaCardDesktopDownloadTool2"),
                    t("pathBuilder.instructionContent.flashJavaCardDesktopDownloadTool3")
                  ]
                }}
                steps={{
                  items: [
                    {
                      content: (
                        <>
                          {t("pathBuilder.instructionContent.flashJavaCardDesktopDownloadStep1", { product: productName })}
                          {isSatochipSelected && (
                            <>
                              <div className="buy-link-container" style={{ marginTop: "10px", marginBottom: "10px" }}>
                                <a 
                                  href="https://github.com/Toporin/SatochipApplet/releases/download/v0.12/SatoChip-0.12-05.cap"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="buy-link"
                                >
                                  {t("pathBuilder.instructionContent.flashJavaCardDesktopDownloadStep1DownloadSatochip")}
                                </a>
                              </div>
                              <p>
                                {t("pathBuilder.instructionContent.flashJavaCardDesktopDownloadStep1Alternative")}{" "}
                                <a 
                                  href="https://github.com/Toporin/SatochipApplet/releases/tag/v0.12"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="external-link"
                                >
                                  {t("pathBuilder.instructionContent.flashJavaCardDesktopDownloadStep1Link", { product: productName })}
                                </a>
                              </p>
                            </>
                          )}
                          {!isSatochipSelected && (
                            <p>
                              <a 
                                href={`${productRepoUrl}/releases`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="external-link"
                              >
                                {t("pathBuilder.instructionContent.flashJavaCardDesktopDownloadStep1Link", { product: productName })}
                              </a>
                            </p>
                          )}
                        </>
                      )
                    },
                    t("pathBuilder.instructionContent.flashJavaCardDesktopDownloadStep2"),
                    {
                      content: (
                        <>
                          {t("pathBuilder.instructionContent.flashJavaCardDesktopDownloadStep3")}
                          {selectedOS === 'Linux' && (
                            <div className="code-block">
                              <code>sudo apt install pcscd pcsc-tools</code>
                            </div>
                          )}
                          {selectedOS === 'Mac' && (
                            <div className="code-block">
                              <code>brew install pcsc-lite</code>
                            </div>
                          )}
                          <div className="code-block">
                            <code>git clone https://github.com/martinpaljak/GlobalPlatformPro.git</code>
                          </div>
                          <div className="code-block">
                            <code>cd GlobalPlatformPro</code>
                          </div>
                          <div className="code-block">
                            <code>./gradlew build</code>
                          </div>
                        </>
                      )
                    },
                    t("pathBuilder.instructionContent.flashJavaCardDesktopDownloadStep4", { product: productName }),
                    {
                      content: (
                        <>
                          {t("pathBuilder.instructionContent.flashJavaCardDesktopDownloadStep5", { product: productName })}
                          {selectedOS === 'Windows' && (
                            <>
                              <p><strong>{t("pathBuilder.instructionContent.flashJavaCardDesktopDownloadStep5WindowsList")}</strong></p>
                              <div className="code-block">
                                <code>gp.exe -l</code>
                              </div>
                              <p><strong>{t("pathBuilder.instructionContent.flashJavaCardDesktopDownloadStep5WindowsInstall")}</strong></p>
                              <div className="code-block">
                                <code>{`gp.exe -install .\\${productCapFileName}`}</code>
                              </div>
                              <p><strong>{t("pathBuilder.instructionContent.flashJavaCardDesktopDownloadStep5WindowsUninstall")}</strong></p>
                              <div className="code-block">
                                <code>{`gp.exe -uninstall .\\${productCapFileName}`}</code>
                              </div>
                            </>
                          )}
                          {selectedOS === 'Linux' && (
                            <>
                              <p><strong>{t("pathBuilder.instructionContent.flashJavaCardDesktopDownloadStep5LinuxList")}</strong></p>
                              <div className="code-block">
                                <code>java -jar gp.jar -l</code>
                              </div>
                              <p><strong>{t("pathBuilder.instructionContent.flashJavaCardDesktopDownloadStep5LinuxInstall")}</strong></p>
                              <div className="code-block">
                                <code>{`java -jar gp.jar -install ./${productCapFileName}`}</code>
                              </div>
                              <p><strong>{t("pathBuilder.instructionContent.flashJavaCardDesktopDownloadStep5LinuxUninstall")}</strong></p>
                              <div className="code-block">
                                <code>{`java -jar gp.jar -uninstall ./${productCapFileName}`}</code>
                              </div>
                            </>
                          )}
                          {selectedOS === 'Mac' && (
                            <>
                              <p><strong>{t("pathBuilder.instructionContent.flashJavaCardDesktopDownloadStep5MacList")}</strong></p>
                              <div className="code-block">
                                <code>java -jar gp.jar -l</code>
                              </div>
                              <p><strong>{t("pathBuilder.instructionContent.flashJavaCardDesktopDownloadStep5MacInstall")}</strong></p>
                              <div className="code-block">
                                <code>{`java -jar gp.jar -install ./${productCapFileName}`}</code>
                              </div>
                              <p><strong>{t("pathBuilder.instructionContent.flashJavaCardDesktopDownloadStep5MacUninstall")}</strong></p>
                              <div className="code-block">
                                <code>{`java -jar gp.jar -uninstall ./${productCapFileName}`}</code>
                              </div>
                            </>
                          )}
                        </>
                      )
                    },
                    t("pathBuilder.instructionContent.flashJavaCardDesktopDownloadStep6")
                  ]
                }}
                customContent={
                <div className="buy-link-container">
                  <a 
                    href="https://www.amazon.com/ACR39U-NF-PocketMate-USB-C-Smart-Reader/dp/B06X9NTGYV" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="buy-link"
                  >
                    {t("pathBuilder.instructionContent.flashJavaCardDesktopCompileLink")}
                  </a>
                </div>
                }
              />
            )}

            {options.verifySignatureJavaCardDesktop && (
              <StandardInstruction
                title={t("pathBuilder.instructionTitles.verifySignatureJavaCardDesktop", { product: productName })}
                icon="🔐"
                steps={{
                  items: [
                    t("pathBuilder.instructionContent.verifySignatureJavaCardDesktopStep1", { product: productName }),
                    t("pathBuilder.instructionContent.verifySignatureJavaCardDesktopStep2", { product: productName }),
                    t("pathBuilder.instructionContent.verifySignatureJavaCardDesktopStep3"),
                    t("pathBuilder.instructionContent.verifySignatureJavaCardDesktopStep4"),
                    t("pathBuilder.instructionContent.verifySignatureJavaCardDesktopStep5")
                  ]
                }}
              />
            )}

            {options.addRemoveApplet && (
              <StandardInstruction
                title={t("pathBuilder.instructionTitles.addRemoveApplet")}
                icon="🔄"
                intro={
                  <ExpandableYouTubeEmbed 
                    videoId="https://youtu.be/N-i2E5VmpXA?si=hLT_rzjaI05oDINj"
                    title={t("pathBuilder.instructionContent.addRemoveAppletVideoLink")}
                  />
                }
                steps={{
                  items: [
                    t("pathBuilder.instructionContent.addRemoveAppletStep1"),
                    t("pathBuilder.instructionContent.addRemoveAppletStep2"),
                    t("pathBuilder.instructionContent.addRemoveAppletStep3"),
                    t("pathBuilder.instructionContent.addRemoveAppletStep4")
                  ]
                }}
              />
            )}

            {options.removeAppletViaSeedSigner && (
              <StandardInstruction
                title={t("pathBuilder.instructionTitles.removeAppletViaSeedSigner")}
                icon="🗑️"
                steps={{
                  items: [
                    t("pathBuilder.instructionContent.removeAppletViaSeedSignerStep1"),
                    {
                      content: (
                        <>
                          {t("pathBuilder.instructionContent.removeAppletViaSeedSignerStep2")}
                          <div style={{ marginTop: '12px' }}>
                            <ImageWithModal
                              src={`${process.env.PUBLIC_URL}/assets/images/seedsigner/images_flash_04.jpg`}
                              alt="SeedSigner device showing Javacard DIY menu with Install Applet and Uninstall Applet options"
                            />
                          </div>
                        </>
                      )
                    },
                    t("pathBuilder.instructionContent.removeAppletViaSeedSignerStep3"),
                    t("pathBuilder.instructionContent.removeAppletViaSeedSignerStep4")
                  ]
                }}
              />
            )}

            {/* JAVACARD - Product-Specific Operations (Seedkeeper) */}
            {options.createSeedkeeper && (
              <StandardInstruction
                title={t("pathBuilder.instructionTitles.createSeedkeeper")}
                icon="🗝️"
                steps={{
                  items: [
                    t("pathBuilder.instructionContent.createSeedkeeperStep1"),
                    t("pathBuilder.instructionContent.createSeedkeeperStep2"),
                    t("pathBuilder.instructionContent.createSeedkeeperStep3"),
                    t("pathBuilder.instructionContent.createSeedkeeperStep4"),
                    t("pathBuilder.instructionContent.createSeedkeeperStep5"),
                    t("pathBuilder.instructionContent.createSeedkeeperStep6"),
                    t("pathBuilder.instructionContent.createSeedkeeperStep7"),
                    t("pathBuilder.instructionContent.createSeedkeeperStep8")
                  ]
                }}
              />
            )}

            {options.useSeedkeeper && (
              <StandardInstruction
                title={t("pathBuilder.instructionTitles.useSeedkeeper")}
                icon="🔐"
                steps={{
                  items: [
                    t("pathBuilder.instructionContent.useSeedkeeperStep1"),
                    t("pathBuilder.instructionContent.useSeedkeeperStep2"),
                    t("pathBuilder.instructionContent.useSeedkeeperStep3"),
                    t("pathBuilder.instructionContent.useSeedkeeperStep4"),
                    t("pathBuilder.instructionContent.useSeedkeeperStep5"),
                    t("pathBuilder.instructionContent.useSeedkeeperStep6"),
                    t("pathBuilder.instructionContent.useSeedkeeperStep7")
                  ]
                }}
              />
            )}

            {options.backupSeedkeeper && (
              <StandardInstruction
                title={t("pathBuilder.instructionTitles.backupSeedkeeper")}
                icon="💾"
                steps={{
                  items: [
                    t("pathBuilder.instructionContent.backupSeedkeeperStep1"),
                    t("pathBuilder.instructionContent.backupSeedkeeperStep2"),
                    t("pathBuilder.instructionContent.backupSeedkeeperStep3"),
                    t("pathBuilder.instructionContent.backupSeedkeeperStep4"),
                    t("pathBuilder.instructionContent.backupSeedkeeperStep5"),
                    t("pathBuilder.instructionContent.backupSeedkeeperStep6"),
                    t("pathBuilder.instructionContent.backupSeedkeeperStep7"),
                    t("pathBuilder.instructionContent.backupSeedkeeperStep8")
                  ]
                }}
              />
            )}

                {options.restoreFromSeedkeeper && (
              <StandardInstruction
                title={t("pathBuilder.instructionTitles.restoreFromSeedkeeper")}
                icon="🔄"
                steps={{
                  items: [
                    t("pathBuilder.instructionContent.restoreFromSeedkeeperStep1"),
                    t("pathBuilder.instructionContent.restoreFromSeedkeeperStep2"),
                    t("pathBuilder.instructionContent.restoreFromSeedkeeperStep3"),
                    t("pathBuilder.instructionContent.restoreFromSeedkeeperStep4"),
                    t("pathBuilder.instructionContent.restoreFromSeedkeeperStep5"),
                    t("pathBuilder.instructionContent.restoreFromSeedkeeperStep6"),
                    t("pathBuilder.instructionContent.restoreFromSeedkeeperStep7"),
                    t("pathBuilder.instructionContent.restoreFromSeedkeeperStep8")
                  ]
                }}
              />
                )}
              </div>
            )}

            <PathBuilderInstructionsSeedkeeper t={t} options={options} />
            <PathBuilderInstructionsSatochip t={t} options={options} />
            <PathBuilderInstructionsWalletConnection t={t} options={options} />

            <PathBuilderInstructionsKeyBackup t={t} options={options} />

          </div>
        </ContentContainer>
      )}
    </div>
  );
}

export default PathBuilder;


